import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives, Transition as _Transition, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createBlock as _createBlock, createElementVNode as _createElementVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue";
const _withScopeId = n => (_pushScopeId("data-v-fce43db2"), n = n(), _popScopeId(), n);
const _hoisted_1 = ["src", "autoplay", "crossorigin"];
const _hoisted_2 = ["src", "srclang", "label"];
const _hoisted_3 = {
  key: 3,
  class: "default-background"
};
const _hoisted_4 = ["innerHTML"];
import { modalController } from '@ionic/vue';
import { sanitize } from 'dompurify';
import { marked } from 'marked';
import removeMarkdown from 'remove-markdown';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AppVideoPlayerContent from '@/components/AppVideoPlayerContent.vue';
import useAdminSettings from '@/composables/global/use-admin-settings';
import useLevelPage from '@/composables/global/use-level-page';
import useShellHeader from '@/composables/global/use-shell-header';
import { downloadFileFromInternalURL, handleFileDownload } from '@/utils/download';
import { errorAlert } from '@/utils/ion-alert';
import { removeCustomMarkdownTooltip, replaceCustomMarkdownTooltip, replaceCustomHTMLTooltip } from '@/utils/strings';
import { getClosedCaptionsPref, setClosedCaptionsPref } from '@/utils/user-preferences';
export default /*#__PURE__*/_defineComponent({
  __name: 'AppVideoPlayer',
  props: {
    src: {},
    autoplay: {
      type: Boolean
    },
    backgroundColor: {},
    closedCaptionSrc: {},
    isPremierMode: {
      type: Boolean
    },
    isMobileView: {
      type: Boolean
    },
    hasAdditionalContent: {
      type: Boolean
    },
    disabledScrubber: {
      type: Boolean
    },
    disabledCrossOrigin: {
      type: Boolean
    },
    forceDefaultPlayer: {
      type: Boolean
    },
    transcript: {},
    transcriptAudioFileSrc: {}
  },
  emits: ["ended", "openTranscript", "hoverTranscriptTooltip"],
  setup(__props, {
    emit: __emit
  }) {
    _useCssVars(_ctx => ({
      "24891832": backgroundColor.value
    }));
    const emit = __emit;
    const props = __props;
    const {
      locale,
      t
    } = useI18n({
      useScope: 'global',
      messages: {
        en: {
          AppVideoPlayer: {
            captionLabel: 'English',
            downloadAudio: 'Download Audio',
            downloadTranscript: 'Download Transcript',
            moreContentBelow: 'More content below',
            transcriptBelow: 'Transcript below',
            hideTranscript: 'Hide transcript',
            showTranscript: 'Show transcript'
          }
        },
        es: {
          AppVideoPlayer: {
            captionLabel: 'Español',
            downloadAudio: 'Descargar audio',
            downloadTranscript: 'Descargar transcripción',
            moreContentBelow: 'Más contenido debajo',
            transcriptBelow: 'Transcripción debajo',
            showTranscript: 'Mostrar transcripción',
            hideTranscript: 'Ocultar transcripción'
          }
        }
      }
    });
    const isUnmounting = ref(false);
    const defaultPlayerRef = ref();
    const inlinePlayerRef = ref();
    const modalPlayerRef = ref();
    const startTime = ref(0);
    const isFullscreen = ref(false);
    const showTranscript = ref(false);
    const event = ref(null);
    const popoverOpen = ref(false);
    const isInlinePlaying = ref(false);
    const isModalPlaying = ref(false);
    const supposedCurrentTime = ref(0);
    const maxCurrentTime = ref(0); // Used to memorize the most max time the video has reached
    const hoverTimeout = ref(undefined);
    const {
      adminEnabled
    } = useAdminSettings();
    const {
      page
    } = useLevelPage();
    const {
      hideStatusBar,
      showStatusBar
    } = useShellHeader();
    const useDefaultVideoPlayer = computed(() => props.forceDefaultPlayer || adminEnabled.value.defaultVideoPlayer);
    const backgroundColor = computed(() => props.backgroundColor || 'var(--ion-color-video-bars)');
    const sanitizedTranscriptMarkdown = computed(() => {
      const preProcessedMarkdown = replaceCustomMarkdownTooltip(props.transcript || '');
      let html = marked(preProcessedMarkdown);
      html = replaceCustomHTMLTooltip(html);
      return sanitize(html);
    });
    function canDismissModal() {
      return isUnmounting.value;
    }
    function toggleInlinePlaying(force) {
      // If force is specified, set playback to its value
      if (force !== undefined) isInlinePlaying.value = force;else isInlinePlaying.value = !isInlinePlaying.value;
    }
    function toggleModalPlaying(force) {
      // If force is specified, set playback to its value
      if (force !== undefined) isModalPlaying.value = force;else isModalPlaying.value = !isModalPlaying.value;
    }
    function toggleFullscreen(continueAtTime) {
      startTime.value = continueAtTime;
      const wasModalPlaying = isModalPlaying.value;
      isModalPlaying.value = !isFullscreen.value && isInlinePlaying.value;
      isInlinePlaying.value = isFullscreen.value && wasModalPlaying;
      isFullscreen.value = !isFullscreen.value;
      if (isFullscreen.value) {
        hideStatusBar();
      } else {
        showStatusBar();
      }
    }
    function handleMediaControlsPlay() {
      if (isFullscreen.value) toggleModalPlaying(true);else toggleInlinePlaying(true);
    }
    function handleVideoEnded() {
      emit('ended');
    }
    function handleVideoTimeUpdate() {
      if (props.disabledScrubber && defaultPlayerRef.value && !defaultPlayerRef.value.seeking) {
        supposedCurrentTime.value = defaultPlayerRef.value.currentTime;
        if (supposedCurrentTime.value > maxCurrentTime.value) {
          maxCurrentTime.value = supposedCurrentTime.value;
        }
      }
    }
    function handleVideoSeeker() {
      var _defaultPlayerRef$val;
      const currentTime = ((_defaultPlayerRef$val = defaultPlayerRef.value) === null || _defaultPlayerRef$val === void 0 ? void 0 : _defaultPlayerRef$val.currentTime) || 0;
      const supposedTime = supposedCurrentTime.value;
      const maxTime = maxCurrentTime.value;
      if (!props.disabledScrubber || defaultPlayerRef.value === undefined) {
        return;
      }
      const delta = currentTime - supposedTime;
      if (delta <= 0.01) {
        return;
      }
      if (currentTime > maxTime) {
        defaultPlayerRef.value.currentTime = maxTime;
        supposedCurrentTime.value = maxTime;
      } else {
        defaultPlayerRef.value.currentTime = supposedTime;
      }
    }
    function handleMediaControlsPause() {
      if (isFullscreen.value) toggleModalPlaying(false);else toggleInlinePlaying(false);
    }
    async function downloadTranscript() {
      if (!props.transcript) return;
      try {
        var _page$value, _page$value2;
        const textWithoutTooltips = removeCustomMarkdownTooltip(props.transcript);
        const text = removeMarkdown(textWithoutTooltips);
        const pageTitle = ((_page$value = page.value) === null || _page$value === void 0 ? void 0 : _page$value.title) || `Page ${((_page$value2 = page.value) === null || _page$value2 === void 0 ? void 0 : _page$value2.num) || ''}`;
        const blob = new Blob([text], {
          type: 'text/plain'
        });
        await handleFileDownload(blob, `transcript-${pageTitle}.txt`);
      } catch (error) {
        errorAlert({
          error
        });
      }
    }
    async function downloadTranscriptAudio() {
      var _page$value3, _page$value4;
      if (!props.transcriptAudioFileSrc) return;
      const pageTitle = ((_page$value3 = page.value) === null || _page$value3 === void 0 ? void 0 : _page$value3.title) || `Page ${((_page$value4 = page.value) === null || _page$value4 === void 0 ? void 0 : _page$value4.num) || ''}`;
      const fileTitle = `transcript-audio-${pageTitle}`;
      try {
        await downloadFileFromInternalURL({
          url: props.transcriptAudioFileSrc,
          name: fileTitle,
          extension: 'mp3'
        });
      } catch (error) {
        errorAlert({
          error
        });
      }
    }
    function openPopover(e) {
      if (!props.transcriptAudioFileSrc) {
        downloadTranscript();
      } else {
        event.value = e;
        popoverOpen.value = true;
      }
    }
    function onHoverStart(event) {
      const target = event.target;
      if (target.classList.contains('rendered-markdown-tooltip')) {
        hoverTimeout.value = setTimeout(() => {
          var _target$textContent;
          emit('hoverTranscriptTooltip', ((_target$textContent = target.textContent) === null || _target$textContent === void 0 ? void 0 : _target$textContent.trim()) || '');
        }, 1000);
      }
    }
    watch(() => showTranscript.value, () => {
      if (showTranscript.value) {
        emit('openTranscript');
      }
    });
    function onHoverEnd() {
      if (hoverTimeout.value) {
        clearTimeout(hoverTimeout.value);
        hoverTimeout.value = undefined;
      }
    }
    onMounted(async () => {
      var _navigator$mediaSessi, _navigator$mediaSessi2;
      // Handle keyboard media controls play/pause
      (_navigator$mediaSessi = navigator.mediaSession) === null || _navigator$mediaSessi === void 0 || _navigator$mediaSessi.setActionHandler('play', handleMediaControlsPlay);
      (_navigator$mediaSessi2 = navigator.mediaSession) === null || _navigator$mediaSessi2 === void 0 || _navigator$mediaSessi2.setActionHandler('pause', handleMediaControlsPause);
      if (useDefaultVideoPlayer.value) {
        var _defaultPlayerRef$val2, _defaultPlayerRef$val3, _defaultPlayerRef$val4;
        //Ended event
        (_defaultPlayerRef$val2 = defaultPlayerRef.value) === null || _defaultPlayerRef$val2 === void 0 || _defaultPlayerRef$val2.addEventListener('ended', () => handleVideoEnded());
        (_defaultPlayerRef$val3 = defaultPlayerRef.value) === null || _defaultPlayerRef$val3 === void 0 || _defaultPlayerRef$val3.addEventListener('timeupdate', () => handleVideoTimeUpdate());
        // Seekerbar/scrubber seeker event (it doesnt work when sliding )
        (_defaultPlayerRef$val4 = defaultPlayerRef.value) === null || _defaultPlayerRef$val4 === void 0 || _defaultPlayerRef$val4.addEventListener('seeking', () => handleVideoSeeker());
      }
      if (defaultPlayerRef.value && defaultPlayerRef.value.textTracks[0]) {
        // Set closed captions enabled/disabled based on user preference
        if (defaultPlayerRef.value.textTracks[0]) {
          defaultPlayerRef.value.textTracks[0].mode = await getClosedCaptionsPref();
        }
        defaultPlayerRef.value.textTracks.addEventListener('change', () => {
          var _defaultPlayerRef$val5, _defaultPlayerRef$val6;
          return (
            // Update user preference for closed captions on change
            setClosedCaptionsPref((_defaultPlayerRef$val5 = (_defaultPlayerRef$val6 = defaultPlayerRef.value) === null || _defaultPlayerRef$val6 === void 0 ? void 0 : _defaultPlayerRef$val6.textTracks[0].mode) !== null && _defaultPlayerRef$val5 !== void 0 ? _defaultPlayerRef$val5 : 'disabled')
          );
        });
      }
    });
    onBeforeUnmount(async () => {
      // Dismiss fullscreen modal when AppVideoPlayer unmounts.
      // Modal display is toggled with v-show rather than present/dismis so that
      // the fullscreen video doesn't have to reload each time modal is opened.
      isUnmounting.value = true;
      if (!useDefaultVideoPlayer.value) {
        await modalController.dismiss();
      }
    });
    return (_ctx, _cache) => {
      const _component_ion_content = _resolveComponent("ion-content");
      const _component_ion_modal = _resolveComponent("ion-modal");
      const _component_ion_text = _resolveComponent("ion-text");
      const _component_ion_col = _resolveComponent("ion-col");
      const _component_ion_row = _resolveComponent("ion-row");
      const _component_ion_icon = _resolveComponent("ion-icon");
      const _component_ion_grid = _resolveComponent("ion-grid");
      const _component_ion_button = _resolveComponent("ion-button");
      const _component_ion_label = _resolveComponent("ion-label");
      const _component_ion_item = _resolveComponent("ion-item");
      const _component_ion_list = _resolveComponent("ion-list");
      const _component_ion_popover = _resolveComponent("ion-popover");
      return _openBlock(), _createElementBlock(_Fragment, null, [useDefaultVideoPlayer.value ? (_openBlock(), _createElementBlock("video", {
        key: 0,
        ref_key: "defaultPlayerRef",
        ref: defaultPlayerRef,
        src: _ctx.src,
        autoplay: _ctx.autoplay,
        playsinline: "",
        controls: "",
        class: _normalizeClass({
          premier: _ctx.isPremierMode,
          'additional-content': _ctx.hasAdditionalContent || _ctx.transcript
        }),
        onEnded: handleVideoEnded,
        crossorigin: _ctx.disabledCrossOrigin ? undefined : 'anonymous'
      }, [_ctx.closedCaptionSrc ? (_openBlock(), _createElementBlock("track", {
        key: 0,
        kind: "captions",
        src: _ctx.closedCaptionSrc,
        srclang: _unref(locale),
        label: _unref(t)('AppVideoPlayer.captionLabel')
      }, null, 8, _hoisted_2)) : _createCommentVNode("", true)], 42, _hoisted_1)) : (_openBlock(), _createElementBlock(_Fragment, {
        key: 1
      }, [_createVNode(AppVideoPlayerContent, {
        ref_key: "inlinePlayerRef",
        ref: inlinePlayerRef,
        src: _ctx.src,
        autoplay: _ctx.autoplay,
        isPremierMode: _ctx.isPremierMode,
        startTime: startTime.value,
        disabledScrubber: _ctx.disabledScrubber,
        closedCaptionSrc: _ctx.closedCaptionSrc,
        backgroundColor: backgroundColor.value,
        disabledCrossOrigin: _ctx.disabledCrossOrigin,
        isMobileView: _ctx.isMobileView,
        isPlaying: isInlinePlaying.value,
        hasAdditionalContent: _ctx.hasAdditionalContent || Boolean(_ctx.transcript),
        onToggleFullscreen: toggleFullscreen,
        onTogglePlaying: toggleInlinePlaying,
        onEnded: handleVideoEnded
      }, null, 8, ["src", "autoplay", "isPremierMode", "startTime", "disabledScrubber", "closedCaptionSrc", "backgroundColor", "disabledCrossOrigin", "isMobileView", "isPlaying", "hasAdditionalContent"]), _createVNode(_Transition, {
        name: "fade",
        onAfterEnter: _cache[0] || (_cache[0] = $event => {
          var _modalPlayerRef$value;
          return (_modalPlayerRef$value = modalPlayerRef.value) === null || _modalPlayerRef$value === void 0 ? void 0 : _modalPlayerRef$value.$el.focus();
        }),
        onAfterLeave: _cache[1] || (_cache[1] = $event => {
          var _inlinePlayerRef$valu;
          return (_inlinePlayerRef$valu = inlinePlayerRef.value) === null || _inlinePlayerRef$valu === void 0 ? void 0 : _inlinePlayerRef$valu.$el.focus();
        })
      }, {
        default: _withCtx(() => [_withDirectives(_createVNode(_component_ion_modal, {
          "is-open": true,
          canDismiss: canDismissModal,
          class: "fullscreen-modal"
        }, {
          default: _withCtx(() => [_createVNode(_component_ion_content, {
            "scroll-y": false
          }, {
            default: _withCtx(() => [_createVNode(AppVideoPlayerContent, {
              ref_key: "modalPlayerRef",
              ref: modalPlayerRef,
              src: _ctx.src,
              startTime: startTime.value,
              backgroundColor: backgroundColor.value,
              isFullscreen: "",
              disabledScrubber: _ctx.disabledScrubber,
              closedCaptionSrc: _ctx.closedCaptionSrc,
              disabledCrossOrigin: _ctx.disabledCrossOrigin,
              isMobileView: _ctx.isMobileView,
              isPlaying: isModalPlaying.value,
              onToggleFullscreen: toggleFullscreen,
              onTogglePlaying: toggleModalPlaying,
              onEnded: handleVideoEnded
            }, null, 8, ["src", "startTime", "backgroundColor", "disabledScrubber", "closedCaptionSrc", "disabledCrossOrigin", "isMobileView", "isPlaying"])]),
            _: 1
          })]),
          _: 1
        }, 512), [[_vShow, isFullscreen.value]])]),
        _: 1
      })], 64)), _ctx.isPremierMode && (_ctx.hasAdditionalContent || _ctx.transcript) ? (_openBlock(), _createBlock(_component_ion_grid, {
        key: 2,
        class: "additional-content-hint ion-no-padding"
      }, {
        default: _withCtx(() => [_createVNode(_component_ion_row, {
          class: "ion-justify-content-center"
        }, {
          default: _withCtx(() => [_createVNode(_component_ion_col, {
            size: "auto"
          }, {
            default: _withCtx(() => [_createVNode(_component_ion_text, {
              class: "ion-text-nowrap"
            }, {
              default: _withCtx(() => [_createTextVNode(_toDisplayString(_unref(t)(`AppVideoPlayer.${_ctx.hasAdditionalContent ? 'moreContentBelow' : 'transcriptBelow'}`)), 1)]),
              _: 1
            })]),
            _: 1
          })]),
          _: 1
        }), _createVNode(_component_ion_row, {
          class: "ion-justify-content-center"
        }, {
          default: _withCtx(() => [_createVNode(_component_ion_col, {
            size: "auto"
          }, {
            default: _withCtx(() => [_createVNode(_component_ion_icon, {
              name: "chevron-down",
              class: "more-content-indicator"
            })]),
            _: 1
          })]),
          _: 1
        })]),
        _: 1
      })) : _createCommentVNode("", true), _ctx.transcript ? (_openBlock(), _createElementBlock("div", _hoisted_3, [_createVNode(_component_ion_grid, {
        class: "ion-padding content-max-width"
      }, {
        default: _withCtx(() => [_createVNode(_component_ion_row, {
          class: "ion-justify-content-center"
        }, {
          default: _withCtx(() => [_createVNode(_component_ion_col, {
            size: "auto"
          }, {
            default: _withCtx(() => [_createVNode(_component_ion_button, {
              onClick: _cache[2] || (_cache[2] = $event => showTranscript.value = !showTranscript.value)
            }, {
              default: _withCtx(() => [_createTextVNode(_toDisplayString(_unref(t)(`AppVideoPlayer.${showTranscript.value ? 'hideTranscript' : 'showTranscript'}`)), 1)]),
              _: 1
            })]),
            _: 1
          })]),
          _: 1
        }), showTranscript.value ? (_openBlock(), _createBlock(_component_ion_row, {
          key: 0,
          class: "ion-justify-content-end"
        }, {
          default: _withCtx(() => [_createVNode(_component_ion_col, {
            size: "auto"
          }, {
            default: _withCtx(() => [_createVNode(_component_ion_button, {
              fill: "outline",
              onClick: openPopover
            }, {
              default: _withCtx(() => [_createVNode(_component_ion_icon, {
                slot: "icon-only",
                icon: "download"
              })]),
              _: 1
            }), _createVNode(_component_ion_popover, {
              "is-open": popoverOpen.value,
              event: event.value,
              "dismiss-on-select": "",
              onDidDismiss: _cache[3] || (_cache[3] = $event => popoverOpen.value = false)
            }, {
              default: _withCtx(() => [_createVNode(_component_ion_content, null, {
                default: _withCtx(() => [_createVNode(_component_ion_list, null, {
                  default: _withCtx(() => [_createVNode(_component_ion_item, {
                    onClick: downloadTranscript,
                    button: ""
                  }, {
                    default: _withCtx(() => [_createVNode(_component_ion_label, {
                      class: "ion-text-wrap"
                    }, {
                      default: _withCtx(() => [_createTextVNode(_toDisplayString(_unref(t)('AppVideoPlayer.downloadTranscript')), 1)]),
                      _: 1
                    }), _createVNode(_component_ion_icon, {
                      icon: "document-text",
                      slot: "end"
                    })]),
                    _: 1
                  }), _createVNode(_component_ion_item, {
                    onClick: downloadTranscriptAudio,
                    button: ""
                  }, {
                    default: _withCtx(() => [_createVNode(_component_ion_label, {
                      class: "ion-text-wrap"
                    }, {
                      default: _withCtx(() => [_createTextVNode(_toDisplayString(_unref(t)('AppVideoPlayer.downloadAudio')), 1)]),
                      _: 1
                    }), _createVNode(_component_ion_icon, {
                      icon: "volume-medium",
                      slot: "end"
                    })]),
                    _: 1
                  })]),
                  _: 1
                })]),
                _: 1
              })]),
              _: 1
            }, 8, ["is-open", "event"])]),
            _: 1
          })]),
          _: 1
        })) : _createCommentVNode("", true), showTranscript.value ? (_openBlock(), _createBlock(_component_ion_row, {
          key: 1
        }, {
          default: _withCtx(() => [_createVNode(_component_ion_col, null, {
            default: _withCtx(() => [_createElementVNode("div", {
              innerHTML: sanitizedTranscriptMarkdown.value,
              class: "rendered-markdown",
              onMouseover: onHoverStart,
              onMouseleave: onHoverEnd
            }, null, 40, _hoisted_4)]),
            _: 1
          })]),
          _: 1
        })) : _createCommentVNode("", true)]),
        _: 1
      })])) : _createCommentVNode("", true)], 64);
    };
  }
});